import React, { useEffect, useState } from 'react';
import { Box, Progress, Text, VStack, Center, Heading, Button, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const MAX_CONCURRENT_PROCESSES = 3; // Number of parallel processes

// Add cache to track processed forms
const processedFormsCache = new Map();

const BulkImportProcessor = ({ userToken }) => {
  const [progress, setProgress] = useState(0);
  const [activeProcesses, setActiveProcesses] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isUpdatingSheet, setIsUpdatingSheet] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const SPREADSHEET_ID = '12U8mahyXjKg1pQTprhuhczA1BYOQ7Z5tnSLzu3-I5z8';
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const loadSheetData = async () => {
    try {
      const response = await window.gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId: SPREADSHEET_ID,
        range: 'Sheet1!A2:B',
      });

      const rows = response.result.values || [];
      console.log('All rows:', rows);  // Debug log
      
      // Store original row indices before filtering
      const rowsWithIndices = rows.map((row, index) => ({ 
        data: row, 
        originalIndex: index 
      }));
      
      // Filter unique rows and keep their original indices
      const uniqueRowsWithIndices = rowsWithIndices.filter((row, index, self) =>
        index === self.findIndex((r) => r.data[0] === row.data[0])
      );
      
      // Filter pending rows but keep their original indices
      const pending = uniqueRowsWithIndices.filter(row => {
        if (!row.data || !Array.isArray(row.data)) return false;
        // Skip if already processed
        if (processedFormsCache.has(row.data[0])) {
          console.log('Skipping already processed form:', row.data[0]);
          return false;
        }
        return row.data[1]?.trim().toUpperCase() !== 'OK';
      });

      console.log('Pending rows with indices:', pending);  // Debug log
      
      // Store pending rows with their original indices
      setPendingRows(pending);
      setTotalRows(uniqueRowsWithIndices.length);
      setProgress(((uniqueRowsWithIndices.length - pending.length) / uniqueRowsWithIndices.length) * 100);
    } catch (error) {
      console.error('Error loading sheet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sheet data',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const updateSheetStatus = async (originalIndex) => {
    try {
      if (!accessToken) {
        throw new Error('No access token available');
      }

      window.gapi.client.setToken({ 
        access_token: accessToken
      });

      const sheetRow = originalIndex + 2;
      
      try {
        const response = await window.gapi.client.sheets.spreadsheets.values.update({
          spreadsheetId: SPREADSHEET_ID,
          range: `Sheet1!B${sheetRow}`,
          valueInputOption: 'USER_ENTERED',
          resource: {
            values: [['OK']]
          }
        });

        if (response.status !== 200) {
          throw new Error(`Failed to update sheet: ${response.statusText}`);
        }

      } catch (apiError) {
        if (apiError.status === 401 || apiError.status === 403) {
          // Handle auth errors by requesting a new token
          setIsAuthenticated(false);
          setAccessToken(null);
          toast({
            title: 'Authentication Expired',
            description: 'Please re-authenticate with Google Sheets',
            status: 'warning',
            duration: 7000,
            isClosable: true,
          });
          throw new Error('Authorization expired');
        }
        throw apiError;
      }

    } catch (error) {
      console.error('Error updating sheet:', error);
      setIsProcessing(false);
      throw error;
    }
  };

  useEffect(() => {
    const loadGapiAndInitialize = () => {
      const initGoogleSheets = async () => {
        try {
          await window.gapi.client.init({
            apiKey: 'AIzaSyDZVE6za6MCTHxSETykx8yU83VMboiHBrs',
            discoveryDocs: ['https://sheets.googleapis.com/$discovery/rest?version=v4'],
          });

          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: '926856425180-2s0ks21obsl67psfra7aij2f0pqv2hbq.apps.googleusercontent.com',
            scope: 'https://www.googleapis.com/auth/spreadsheets',
            prompt: 'consent',
            callback: async (response) => {
              if (response.access_token) {
                setAccessToken(response.access_token);
                window.gapi.client.setToken({
                  access_token: response.access_token
                });
                setIsAuthenticated(true);
                await loadSheetData();
              }
            },
            error_callback: (error) => {
              console.error('Auth error:', error);
              setIsAuthenticated(false);
              setAccessToken(null);
              localStorage.removeItem('gsheet_refresh_token');
              toast({
                title: 'Authentication Error',
                description: 'Failed to authenticate with Google',
                status: 'error',
                duration: 5000,
              });
            }
          });

          setTokenClient(client);
        } catch (error) {
          console.error('Error initializing Google Sheets:', error);
          toast({
            title: 'Error',
            description: 'Failed to initialize Google Sheets',
            status: 'error',
            duration: 5000,
          });
        }
      };

      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.onload = () => {
        const gapiScript = document.createElement('script');
        gapiScript.src = 'https://apis.google.com/js/api.js';
        gapiScript.onload = () => {
          window.gapi.load('client', initGoogleSheets);
        };
        document.body.appendChild(gapiScript);
      };
      document.body.appendChild(gisScript);
    };

    loadGapiAndInitialize();

    return () => {
      const scripts = document.querySelectorAll('script[src*="googleapis.com"], script[src*="accounts.google.com"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  const handleAuthClick = () => {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt: 'consent' });
    }
  };

  const startNextImport = async () => {
    if (!pendingRows.length) {
      setIsProcessing(false);
      return;
    }

    // Double-check we haven't exceeded the limit
    if (activeProcesses.length >= MAX_CONCURRENT_PROCESSES) {
      console.log('Maximum concurrent processes reached:', activeProcesses.length);
      return;
    }

    // Create a Set of all prompts that are either active or pending
    const activePrompts = new Set(activeProcesses.map(p => p.prompt));
    
    // Log current state for debugging
    console.log('Current state:', {
      activeProcesses: activeProcesses.length,
      pendingRows: pendingRows.length,
      activePrompts: Array.from(activePrompts)
    });

    // Get the next batch of rows to process, with strict filtering
    const nextBatch = pendingRows
      .filter(row => {
        const isDuplicate = activePrompts.has(row.data[0]) || processedFormsCache.has(row.data[0]);
        if (isDuplicate) {
          console.log('Skipping duplicate prompt:', row.data[0]);
        }
        return !isDuplicate;
      })
      .slice(0, MAX_CONCURRENT_PROCESSES - activeProcesses.length);
    
    if (nextBatch.length === 0) {
      console.log('No new rows to process after filtering');
      return;
    }

    try {
      const newProcesses = await Promise.all(
        nextBatch.map(async (row) => {
          // Double-check this prompt isn't being processed
          if (activePrompts.has(row.data[0]) || processedFormsCache.has(row.data[0])) {
            console.log('Skipping duplicate prompt during processing:', row.data[0]);
            return null;
          }

          const message = "Construye un formulario con la siguiente informacion, debes retornar como output un JSON limpio y completo listo para procesar con comando JSON.parse de javascript, sin comentarios o cosas pendientes de implementar.  Utiliza el siguiente contenido para crear del formulario: \n\n" + row.data[0];
          
          try {
            const aiResponse = await axios.post(`${backendUrl}runOpenAIAssistant`, {
              message,
              userToken
            });

            // Add to cache immediately to prevent duplicate processing
            processedFormsCache.set(row.data[0], true);

            return {
              runId: aiResponse.data.id,
              threadId: aiResponse.data.thread_id,
              prompt: row.data[0],
              originalIndex: row.originalIndex,
              startTime: Date.now(),
              retryCount: 0
            };
          } catch (error) {
            console.error('Error starting process for prompt:', row.data[0], error);
            // If it's a 500 error, mark this row as failed and remove it from pending
            if (error.response?.status === 500) {
              setPendingRows(prev => prev.filter(r => r.originalIndex !== row.originalIndex));
              toast({
                title: 'Error',
                description: `Failed to process form: ${row.data[0]}`,
                status: 'error',
                duration: 5000,
              });
            }
            return null;
          }
        })
      );

      // Filter out any null processes (duplicates or errors)
      const validNewProcesses = newProcesses.filter(Boolean);

      // Update states atomically with validation
      setActiveProcesses(prev => {
        const newActive = [...prev, ...validNewProcesses];
        // Final safety check
        if (newActive.length > MAX_CONCURRENT_PROCESSES) {
          console.error('Safety check: Would exceed max processes, truncating');
          return newActive.slice(0, MAX_CONCURRENT_PROCESSES);
        }
        console.log('New active processes count:', newActive.length);
        return newActive;
      });

      setPendingRows(prev => {
        const newPending = prev.filter(row => 
          !validNewProcesses.some(process => process.prompt === row.data[0])
        );
        console.log('New pending rows count:', newPending.length);
        return newPending;
      });
    } catch (error) {
      console.error('Error starting imports:', error);
      toast({
        title: 'Error',
        description: 'Failed to start some imports',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const checkStatus = async () => {
    if (activeProcesses.length === 0 || isUpdatingSheet) return;

    try {
      const statusChecks = activeProcesses.map(async (process) => {
        try {
          const statusResponse = await axios.get(`${backendUrl}checkStatus`, {
            params: {
              runID: process.runId,
              threadID: process.threadId,
              userToken
            }
          });

          if (statusResponse.data.success) {
            return process;
          }
          return null;
        } catch (error) {
          console.error(`Error checking status for process ${process.runId}:`, error);
          return null;
        }
      });

      const completedProcesses = (await Promise.all(statusChecks)).filter(Boolean);

      if (completedProcesses.length > 0) {
        setIsUpdatingSheet(true);
        
        try {
          // Update sheet for each completed process
          await Promise.all(
            completedProcesses.map(async (process) => {
              await updateSheetStatus(process.originalIndex);
            })
          );

          // Remove completed processes with validation
          setActiveProcesses(prev => {
            const newActive = prev.filter(p => 
              !completedProcesses.some(cp => cp.runId === p.runId)
            );
            console.log('Remaining active processes after completion:', newActive.length);
            return newActive;
          });

          // Update progress
          setProgress(((totalRows - pendingRows.length - activeProcesses.length) / totalRows) * 100);

          // Start new processes if needed, with strict validation
          if (pendingRows.length > 0 && activeProcesses.length < MAX_CONCURRENT_PROCESSES) {
            console.log('Starting next batch after completion');
            setTimeout(() => startNextImport(), 1000);
          } else if (activeProcesses.length === 0 && pendingRows.length === 0) {
            console.log('All processes completed');
            setIsProcessing(false);
          }

        } catch (error) {
          console.error('Failed to update sheet:', error);
          toast({
            title: 'Error',
            description: 'Failed to update sheet. Please try again.',
            status: 'error',
            duration: 5000,
          });
        } finally {
          setIsUpdatingSheet(false);
        }
      } else {
        setTimeout(checkStatus, 5000);
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setIsUpdatingSheet(false);
      setTimeout(checkStatus, 5000);
    }
  };

  useEffect(() => {
    if (isProcessing && activeProcesses.length < MAX_CONCURRENT_PROCESSES && pendingRows.length > 0) {
      console.log('Effect triggered - Starting new processes');
      console.log('Current active:', activeProcesses.length, 'Pending:', pendingRows.length);
      startNextImport();
    }
  }, [isProcessing, activeProcesses.length, pendingRows.length]);

  useEffect(() => {
    if (activeProcesses.length > 0 && !isUpdatingSheet) {
      checkStatus();
    }
  }, [activeProcesses.length, isUpdatingSheet]);

  return (
    <Center flexDirection="column" p={8}>
      <VStack spacing={6} width="100%" maxW="600px">
        <Heading size="lg">Bulk Import Progress</Heading>
        
        {!isAuthenticated ? (
          <Button
            colorScheme="blue"
            onClick={handleAuthClick}
          >
            Authenticate with Google Sheets
          </Button>
        ) : (
          <>
            <Box width="100%">
              <Progress value={progress} size="lg" colorScheme="blue" />
              <Text mt={2} textAlign="center">
                {Math.round(progress)}% Complete ({totalRows - pendingRows.length - activeProcesses.length}/{totalRows})
              </Text>
              <Text mt={2} textAlign="center">
                Active Processes: {activeProcesses.length}/{MAX_CONCURRENT_PROCESSES}
              </Text>
              <Text mt={2} textAlign="center">
                Pending Forms: {pendingRows.length}
              </Text>
            </Box>

            {activeProcesses.length > 0 && (
              <Box>
                <Text fontWeight="bold">Currently Processing:</Text>
                {activeProcesses.map((process, index) => (
                  <Text key={process.runId}>
                    {index + 1}. {process.prompt} (Started: {Math.floor((Date.now() - process.startTime) / 1000)}s ago)
                  </Text>
                ))}
              </Box>
            )}

            <Button
              colorScheme="blue"
              onClick={() => setIsProcessing(true)}
              isDisabled={isProcessing || pendingRows.length === 0}
            >
              {isProcessing ? 'Processing...' : pendingRows.length === 0 ? 'Complete' : 'Resume Import'}
            </Button>
          </>
        )}
      </VStack>
    </Center>
  );
};

export default BulkImportProcessor; 